import type { LlmContext } from "../shared-types.js";

export const SYSTEM_PROMPT = `You are OpenMagic, an AI coding assistant embedded in a developer's web application. You help modify the codebase based on visual context from the running app.

## Your Role
- You can see the developer's running web application (DOM elements, screenshots, styles)
- You propose code modifications to their source files
- Your changes are applied directly to their codebase and reflected via hot reload

## Response Format
You MUST respond with valid JSON in this exact format:

\`\`\`json
{
  "modifications": [
    {
      "file": "relative/path/to/file.tsx",
      "type": "edit",
      "search": "exact code to find (multi-line ok)",
      "replace": "replacement code"
    }
  ],
  "explanation": "Brief description of what was changed and why"
}
\`\`\`

## Modification Types
- \`edit\`: Replace existing code. \`search\` must match exactly in the file. \`replace\` is the new code.
- \`create\`: Create a new file. Use \`content\` instead of search/replace.
- \`delete\`: Delete a file. No search/replace/content needed.

## Rules
1. If the user is asking a QUESTION (not requesting a change), respond with {"modifications":[],"explanation":"your answer here"}. Only propose modifications when the user explicitly wants something changed.
1b. If the request is genuinely ambiguous (multiple plausible files/targets and getting it wrong would edit the wrong code), respond with {"modifications":[],"explanation":"a short clarifying question"} instead of guessing. Follow the Repo Conventions block when present.
2. NEVER use delete+create to modify an existing file. Always use edit with search/replace. Only use create for genuinely new files that don't exist yet.
3. Copy the search string EXACTLY from the grounded source files — do not retype, reformat, or change whitespace/indentation
4. Include 3-5 lines of surrounding context in the search field to ensure uniqueness
5. Keep modifications minimal — change only what's needed. Do NOT rewrite entire files.
6. If the grounded files don't contain the code you need to modify, return: {"modifications":[],"explanation":"NEED_FILE: exact/relative/path/to/file.ext"}
7. To search for a pattern across the codebase, return: {"modifications":[],"explanation":"SEARCH_FILES: \\"pattern\\" in optional/path/"}
8. For style changes: check the dependencies (package.json) to know if the project uses Tailwind, MUI, etc. Use the project's styling approach, not raw CSS
9. Use the selected element's cssSelector, className, parentContainerStyles, and siblings to understand the full layout context
10. Use the page URL route and componentHint to identify the correct source file to modify
11. Use childrenLayout pixel measurements for spacing. gapToNext.vertical=0 means elements are touching.
12. Use resolvedClasses to see actual CSS values behind utility classes (space-y-6 = margin-top: 1.5rem).
13. Check themeState.darkMode — if true, use dark-mode-aware colors and respect the project's dark mode classes.
14. Use cssVariables to leverage existing design tokens (var(--color-primary)) instead of hardcoding hex values.
15. Check activeBreakpoints to know which responsive breakpoint is active. Suggest responsive-aware changes.
16. Check visibilityState — the element may be scrolled out of view, hidden, or inside a scrollable container; parentScrollContainer identifies the nearest scrolling ancestor.
17. If Runtime Errors are present, they are uncaught exceptions or framework error-overlay output from the live app — they usually point directly at the bug. Prioritize fixing them and use the stack/source location to find the file.
18. Always preserve existing code style, conventions, and indentation
19. ALWAYS respond with valid JSON only — no text before or after the JSON object`;

// H9: plain instruction for CLI agents in native-edit mode. They edit the working
// tree directly (we capture + revert + route through review), so NO JSON contract.
export const NATIVE_EDIT_INSTRUCTION = `You are OpenMagic, working directly in the developer's codebase with full file access.
Make the requested change by editing the project's files directly. Investigate as needed, keep changes minimal, and match the existing code style and conventions.
Do NOT output JSON or diffs — just edit the files. When finished, briefly summarize what you changed.`;

export function buildContextParts(context: LlmContext): Parameters<typeof buildUserMessage>[1] {
  const parts: Parameters<typeof buildUserMessage>[1] = {};

  // Send FULL element context with all available signals
  if (context.selectedElement) {
    const el = context.selectedElement as any;
    const elementData: Record<string, unknown> = {
      cssSelector: el.cssSelector,
      tagName: el.tagName,
      id: el.id,
      className: el.className,
      outerHTML: el.outerHTML,
      computedStyles: el.computedStyles,
      ancestry: el.ancestry,
      componentHint: el.componentHint,
    };
    // Parent container styles (layout context)
    if (el.parentStyles && Object.keys(el.parentStyles).length) {
      elementData.parentContainerStyles = el.parentStyles;
    }
    // Sibling elements (what else is in the same container)
    if (el.siblings?.length) {
      elementData.siblings = el.siblings;
    }
    // Matched CSS rules from stylesheets
    if (el.matchedCssRules?.length) {
      elementData.matchedCssRules = el.matchedCssRules;
    }
    // Viewport dimensions
    if (el.viewport) {
      elementData.viewport = el.viewport;
    }
    // Accessibility attributes
    if (el.ariaAttributes && Object.keys(el.ariaAttributes).length) {
      elementData.ariaAttributes = el.ariaAttributes;
    }
    // Event handlers
    if (el.eventHandlers?.length) {
      elementData.eventHandlers = el.eventHandlers;
    }
    // React props
    if (el.reactProps) {
      elementData.reactProps = el.reactProps;
    }
    // Children layout measurements (pixel-level spacing between children)
    if (el.childrenLayout?.length) {
      elementData.childrenLayout = el.childrenLayout;
    }
    // Resolved Tailwind/utility classes to actual CSS values
    if (el.resolvedClasses?.length) {
      elementData.resolvedClasses = el.resolvedClasses;
    }
    // Deep element intelligence
    if (el.themeState) elementData.themeState = el.themeState;
    if (el.cssVariables && Object.keys(el.cssVariables).length) elementData.cssVariables = el.cssVariables;
    if (el.stackingContext) elementData.stackingContext = el.stackingContext;
    if (el.visibilityState) elementData.visibilityState = el.visibilityState;
    if (el.activeBreakpoints?.length) elementData.activeBreakpoints = el.activeBreakpoints;
    if (el.pseudoElements && (el.pseudoElements.before !== "none" || el.pseudoElements.after !== "none")) {
      elementData.pseudoElements = el.pseudoElements;
    }
    if (el.formState) elementData.formState = el.formState;
    parts.selectedElement = JSON.stringify(elementData, null, 2);
  }

  // U3: a concise summary of every selected element when multi-selecting.
  if ((context as any).selectedElements?.length) {
    parts.selectedElementsSummary = ((context as any).selectedElements as any[])
      .map((el, i) => `${i + 1}. <${el.tagName}${el.id ? "#" + el.id : ""}> ${el.cssSelector || ""} ${(el.textContent || "").slice(0, 60)}`.trim())
      .join("\n");
  }

  if (context.files?.length) {
    parts.files = context.files;
  }
  if (context.projectTree) parts.projectTree = context.projectTree;
  if ((context as any).repoConventions) parts.repoConventions = (context as any).repoConventions;
  if ((context as any).pageUrl) parts.pageUrl = (context as any).pageUrl;
  if ((context as any).pageTitle) parts.pageTitle = (context as any).pageTitle;
  if (context.networkLogs) parts.networkLogs = context.networkLogs.map(l => `${l.method} ${l.url} → ${l.status || "pending"}`).join("\n");
  if (context.consoleLogs) parts.consoleLogs = context.consoleLogs.map(l => `[${l.level}] ${l.args.join(" ")}`).join("\n");
  if (context.runtimeErrors?.length) {
    parts.runtimeErrors = context.runtimeErrors
      .map(e => `[${e.type}] ${e.message}${e.source ? ` (${e.source})` : ""}${e.stack ? `\n${e.stack}` : ""}`)
      .join("\n\n");
  }
  if ((context as any).searchResults?.length) {
    parts.searchResults = (context as any).searchResults.map(
      (s: any) => `Search: "${s.query}"\n${s.matches.map((m: any) => `  ${m.file}:${m.lineNum}: ${m.line}`).join("\n")}`
    ).join("\n\n");
  }
  return parts;
}

export function buildUserMessage(
  userPrompt: string,
  context: {
    selectedElement?: string;
    selectedElementsSummary?: string;
    screenshot?: string;
    files?: Array<{ path: string; content: string }>;
    fileContent?: string;
    filePath?: string;
    networkLogs?: string;
    consoleLogs?: string;
    runtimeErrors?: string;
    projectTree?: string;
    repoConventions?: string;
    pageUrl?: string;
    pageTitle?: string;
    searchResults?: string;
  }
): string {
  const parts: string[] = [];

  // Page context — helps LLM find the right route/page component
  if (context.pageUrl || context.pageTitle) {
    parts.push(`## Page Context\nURL: ${context.pageUrl || "unknown"}\nTitle: ${context.pageTitle || "unknown"}`);
  }

  if (context.projectTree) {
    parts.push(`## Project Structure\n\`\`\`\n${context.projectTree}\n\`\`\``);
  }

  if (context.repoConventions) {
    parts.push(`## Repo Conventions (match these)\n\`\`\`\n${context.repoConventions}\n\`\`\``);
  }

  // Grounded source files
  if (context.files?.length) {
    parts.push(`## Grounded Source Files\n${context.files.map(f => `### ${f.path}\n\`\`\`\n${f.content}\n\`\`\``).join("\n\n")}`);
  } else if (context.filePath && context.fileContent) {
    parts.push(`## Source File: ${context.filePath}\n\`\`\`\n${context.fileContent}\n\`\`\``);
  }

  // Selected element — full context including selector, styles, ancestry
  if (context.selectedElement) {
    parts.push(`## Selected Element\n\`\`\`json\n${context.selectedElement}\n\`\`\``);
  }

  if (context.selectedElementsSummary) {
    parts.push(`## All Selected Elements (multi-select)\n\`\`\`\n${context.selectedElementsSummary}\n\`\`\``);
  }

  if (context.networkLogs) {
    parts.push(`## Recent Network Requests\n\`\`\`\n${context.networkLogs}\n\`\`\``);
  }

  if (context.consoleLogs) {
    parts.push(`## Console Output\n\`\`\`\n${context.consoleLogs}\n\`\`\``);
  }

  if (context.runtimeErrors) {
    parts.push(`## Runtime Errors (uncaught — likely the bug to fix)\n\`\`\`\n${context.runtimeErrors}\n\`\`\``);
  }

  if (context.searchResults) {
    parts.push(`## Search Results\n\`\`\`\n${context.searchResults}\n\`\`\``);
  }

  parts.push(`## User Request\n${userPrompt}`);

  return parts.join("\n\n");
}
